import { Button } from '@mui/material';
import { forwardRef, Ref, useEffect, useImperativeHandle, useRef } from 'react';

import { EditPropertyForm, EditPropertyFormRef, EditPropertyFormValues } from './EditPropertyForm';

import { Dialog, DialogRef } from '@/components/common/Dialog';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useProperties } from '@/hooks/properties';
import { Property } from '@/models/Property';
import { useTranslationPrefix } from '@/translations/i18n';

export type EditPropertyDialogRef = DialogRef;

export interface EditPropertyDialogProps<T extends EditPropertyFormValues> {
  /**
   * The property being edited.
   */
  property: Property;
  /**
   * Callback fired when the form is submitted.
   */
  onSubmit?: (values: T) => void;
  /**
   * Whether the dialog is loading or not. If true, the dialog will be disabled and the submit button will be loading.
   */
  isLoading?: boolean;
}

const EditPropertyDialogComponent = <T extends EditPropertyFormValues>(
  { property, onSubmit, isLoading }: EditPropertyDialogProps<T>,
  ref: Ref<EditPropertyDialogRef>,
) => {
  const t = useTranslationPrefix(`edit_property`);
  const toastT = useTranslationPrefix('toasts');
  const { showAlert } = useSnackbar();
  const { editProperty, isEditingProperty, isEditPropertySuccess, editPropertyError } =
    useProperties();
  const dialogRef = useRef<DialogRef>(null);
  const formRef = useRef<EditPropertyFormRef<T>>(null);
  const lastErrorRef = useRef<unknown>(null);

  const resolvedIsLoading = isLoading ?? isEditingProperty;

  const onFormSubmit = (values: T) => {
    if (onSubmit) {
      onSubmit(values);
    } else {
      editProperty({ propertyId: property.id, name: values.propertyName });
    }
  };

  const resetForm = () => {
    formRef.current?.resetForm();
  };

  useEffect(() => {
    if (!isEditingProperty && isEditPropertySuccess) {
      showAlert(toastT('property.saved'), 'success');
      dialogRef.current?.close();
    }
  }, [isEditingProperty, isEditPropertySuccess, showAlert, toastT]);

  useEffect(() => {
    if (editPropertyError && editPropertyError !== lastErrorRef.current) {
      lastErrorRef.current = editPropertyError;
      showAlert(toastT('property.save_failed'), 'error');
    }
  }, [editPropertyError, showAlert, toastT]);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useImperativeHandle(ref, () => dialogRef.current!);

  return (
    <Dialog
      ref={dialogRef}
      title={t('title')}
      actions={
        <>
          <Button
            size="small"
            fullWidth
            variant="outlined"
            disabled={isLoading}
            onClick={() => dialogRef.current?.close()}>
            {t('cancel')}
          </Button>
          <Button
            size="small"
            fullWidth
            loading={resolvedIsLoading}
            onClick={() => formRef.current?.submitForm()}>
            {t('submit')}
          </Button>
        </>
      }
      onClose={resetForm}
      disableClose={resolvedIsLoading}
      error={editPropertyError}>
      <EditPropertyForm property={property} ref={formRef} onSubmit={onFormSubmit} />
    </Dialog>
  );
};

export const EditPropertyDialog = forwardRef(EditPropertyDialogComponent) as <
  T extends EditPropertyFormValues,
>(
  props: EditPropertyDialogProps<T> & { ref?: Ref<EditPropertyDialogRef> },
) => ReturnType<typeof EditPropertyDialogComponent<T>>;
